import { contentListSearchFrom, validateContentListSearch } from "@dashboard/lib/content/content-list-search"
import { contentDocumentQueryOptions, contentListQueryOptions } from "@dashboard/lib/content/content-queries"
import { getContentDocument, listContentDocuments } from "@dashboard/lib/content/content-server"
import { dashboardRolesQueryOptions, dashboardUsersQueryOptions } from "@dashboard/lib/users/users-queries"
import {
  contentSectionRouteFromLocation,
  dashboardContentTypeFromPathSegment,
  dashboardPathForSection,
  dashboardSectionFromPathSegment,
  defaultDashboardContentType,
  newsletterCampaignRouteFromLocation,
} from "@dashboard/routes/dashboard-navigation"
import { LazyDashboardApp } from "@dashboard/routes/lazy-dashboard-app"
import { dashboardAppHostRedirectUrl } from "@playbrain/config/dashboard-host"
import { currentRequestHostname } from "@playbrain/config/dashboard-host-runtime"
import { createFileRoute, redirect, useLocation } from "@tanstack/react-router"
import {
  DashboardSectionSkeleton,
  dashboardSectionSkeletonChrome,
} from "@ui-dashboard/DashboardSectionSkeleton"
import { DashboardShellSkeleton } from "@ui-dashboard/DashboardShellSkeleton"
import { Suspense } from "react"

/**
 * One splat route for a section's list and its editor.
 *
 * The editor pages could have been sibling file routes, but sibling routes
 * mean separate route components, and swapping route components remounts the
 * dashboard beneath them — which drops whatever the shell is holding, most
 * visibly the "saved" toast shown while navigating back to the list. Contract
 * Maker solved the same problem the same way: one route, and the path parsed
 * into a view by `contentSectionRouteFromLocation`.
 */
export const Route = createFileRoute("/dashboard/$section/$")({
  component: DashboardSectionSplatRoute,
  /**
   * The list's filters, as URL state.
   *
   * They used to be Jotai atoms, which meant the server had no way of knowing
   * which list a request wanted — so the table could not start loading until
   * the browser had mounted the app and the atoms had said. Declaring them here
   * is what lets the loader below resolve the rows alongside the document.
   *
   * The editor paths carry the same parameters, deliberately: they are the
   * list state to restore when the editor closes.
   */
  validateSearch: validateContentListSearch,
  beforeLoad: async ({ location, params }) => {
    const redirectHref = dashboardAppHostRedirectUrl(await currentRequestHostname(), location.href)
    if (redirectHref) throw redirect({ href: redirectHref })

    const section = dashboardSectionFromPathSegment(params.section)
    if (!section) {
      throw redirect({ href: dashboardPathForSection(defaultDashboardContentType) })
    }

    /*
     * A suffix only means something to the sections that have an editor — it is
     * that editor's address. Everywhere else, and for a suffix the parsers do
     * not recognise, the canonical single-segment URL is restored rather than
     * serving the section under an address that says something more specific.
     */
    const sectionPath = dashboardPathForSection(section)
    if (location.pathname.replace(/\/+$/, "") !== sectionPath) {
      const contentRoute = contentSectionRouteFromLocation({ pathname: location.pathname })
      const newsletterRoute = newsletterCampaignRouteFromLocation({ pathname: location.pathname })
      if (contentRoute?.type !== "editor" && newsletterRoute?.type !== "editor") {
        // The search rides along: a stale link's PATH may be wrong while its
        // filters are perfectly valid list state.
        throw redirect({ href: `${sectionPath}${location.searchStr}` })
      }
    }
  },
  loaderDeps: ({ search }) => contentListSearchFrom(search),
  /**
   * Two things start here, and the order matters to neither.
   *
   * The chunk imports were already here: starting them while the route
   * resolves moves them alongside the document instead of after it, so the
   * fallback below usually never renders.
   *
   * The list data is new. Fetching it in the loader is what the public routes
   * have always done — see `$locale/news/index.tsx` — and it removes a whole
   * round trip from first paint: the rows now travel with the HTML instead of
   * waiting for hydration to ask for them.
   */
  loader: async ({ context, deps, location, params }) => {
    const chunks =
      typeof window === "undefined"
        ? null
        : (void import("@dashboard/routes/DashboardApp"),
          void import("@dashboard/components/navigation/dashboard-sections"),
          null)

    /*
     * The admin sections start their fetch here too.
     *
     * They used to fall out of this loader before it reached the prefetch, so
     * Users and Roles only began asking for data once the browser had mounted
     * the view — a whole round trip after the content sections, which start
     * theirs alongside the chunk. Nothing is awaited on the client, exactly as
     * below: the fetch is begun, not waited for.
     */
    const section = dashboardSectionFromPathSegment(params.section)
    if (section === "users" || section === "roles") {
      const admin =
        section === "users"
          ? context.queryClient.ensureQueryData(dashboardUsersQueryOptions())
          : context.queryClient.ensureQueryData(dashboardRolesQueryOptions())
      const settled = admin.catch(() => undefined)
      if (typeof window === "undefined") await settled
      return chunks
    }

    const contentType = dashboardContentTypeFromPathSegment(params.section)
    if (!contentType) return chunks

    const contentRoute = contentSectionRouteFromLocation({ pathname: location.pathname })

    /*
     * The editor knows its document from the path, so unlike Contract Maker's
     * admin editor the fetch can start here — and on the server it is awaited,
     * which is what puts the record in the HTML for a deep link. A failure
     * falls through for the same reason as the list below: the editor asks
     * again on mount and renders the error where it belongs.
     */
    if (contentRoute?.type === "editor") {
      if (typeof window !== "undefined") {
        void import("@dashboard/components/content/ContentEditorScreen")
      }
      if (!contentRoute.documentId) return chunks

      const document = context.queryClient
        .ensureQueryData(
          contentDocumentQueryOptions({
            fetchDocument: (id) => getContentDocument({ data: { id } }),
            id: contentRoute.documentId,
          })
        )
        .catch(() => undefined)

      if (typeof window === "undefined") await document
      return chunks
    }

    /**
     * A failure here must not take the section down with it. Someone without
     * read permission on this type, or a backend having a bad moment, should
     * still get the app — which will ask again on mount and render the error
     * in the table where it belongs, exactly as it did before this prefetch
     * existed.
     */
    const rows = context.queryClient
      .ensureQueryData(
        contentListQueryOptions({
          fetchDocuments: (input) => listContentDocuments({ data: input }),
          language: deps.language,
          page: deps.page,
          pageSize: deps.pageSize,
          query: deps.query,
          status: deps.status,
          team: deps.team,
          type: contentType,
        })
      )
      .catch(() => undefined)

    /**
     * Awaited for the document, not for a tab change.
     *
     * A loader holds the navigation open until it resolves, and the router
     * keeps rendering the section you are leaving until it does. On the server
     * that is the whole point: waiting is what puts the rows in the HTML and
     * saves a round trip on a cold load. In the browser it is the opposite —
     * the app is already mounted, the table has a skeleton and a `useQuery`
     * that will fill it, and blocking here only means the old section sits on
     * screen with both tabs lit while the fetch runs.
     *
     * So the fetch starts either way, and only the server waits for it.
     */
    if (typeof window === "undefined") await rows

    return chunks
  },
})

function DashboardSectionSplatRoute() {
  const { section } = Route.useParams()
  // Selected down to the pathname: a bare useLocation() subscribes to every
  // navigation, and the list's search box navigates per keystroke — each
  // character would re-render this component and the whole app below it.
  const pathname = useLocation({ select: (current) => current.pathname })
  const dashboardSection = dashboardSectionFromPathSegment(section) ?? defaultDashboardContentType
  const contentRoute = dashboardContentTypeFromPathSegment(section)
    ? contentSectionRouteFromLocation({ pathname })
    : null

  /**
   * The chrome as well as the page.
   *
   * This boundary suspends on `LazyDashboardApp`, which renders the sidebar,
   * the service tabs and the section — all of it. The fallback stood in for
   * the section alone, so while that chunk arrived there was a table-shaped
   * placeholder floating with no sidebar beside it and no tabs above it, and
   * then the chrome appeared around it. A placeholder has to be the shape of
   * what is missing.
   *
   * The section skeleton is passed straight through to the same slot the shell
   * gives the page, and the boundary inside the app uses that same component —
   * so when the chunk lands, the chrome fills in around content that does not
   * move, rather than one placeholder being swapped for another. The editor is
   * a form, not a table, so its placeholder reserves the header alone.
   */
  return (
    <Suspense
      fallback={
        <DashboardShellSkeleton>
          {contentRoute?.type === "editor" ? (
            <DashboardSectionSkeleton table={false} />
          ) : (
            <DashboardSectionSkeleton {...dashboardSectionSkeletonChrome(dashboardSection)} />
          )}
        </DashboardShellSkeleton>
      }
    >
      <LazyDashboardApp activeSection={dashboardSection} />
    </Suspense>
  )
}
